import { ArrowRight, UserPlus, MousePointerClick, ExternalLink, Check } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { Badge } from "@/components/ui/badge";

export const HowItWorks = () => {
  const steps = [
    {
      icon: <UserPlus className="w-8 h-8 text-corporate-white" strokeWidth={1.25} />,
      title: "Sign Up for Free",
      description: "Create your free account in seconds with no credit card required.",
    }, 
    {
      icon: <MousePointerClick className="w-8 h-8 text-corporate-white" strokeWidth={1.25} />,
      title: "Select a Strategy",
      description: "Choose from verified trading strategies that match your goals.",
    }, 
    { 
      icon: <ExternalLink className="w-8 h-8 text-corporate-white" strokeWidth={1.25} />,
      title: "Connect & Start",
      description: "Select your strategy and begin automated trading immediately.",
    }
  ];

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };
  
  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  return (
    <section className="py-24 px-4 bg-corporate-white border-t border-b border-corporate-neutral-25">
      <div className="container mx-auto">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto"
        >
          <div className="flex justify-center mb-6">
            <Badge className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-corporate-primary-25 text-corporate-primary-100 text-sm font-medium">
              <Check className="w-4 h-4" strokeWidth={1.5} />
              <span>Three Simple Steps to Start</span>
            </Badge>
          </div>
          
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-8 text-corporate-primary-100 leading-tight">
            How METRADE Works
          </h2>
          <p className="text-lg text-center text-corporate-neutral-75 mb-16 max-w-2xl mx-auto">
            Start copy trading in three simple steps. Our platform makes it easy to get started
            and begin your automated crypto trading journey today.
          </p>
          
          <motion.div 
            variants={container}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="grid gap-8 md:grid-cols-3 mb-16 relative"
          >
            {steps.map((step, index) => (
              <motion.div 
                key={index}
                variants={item}
                className="relative h-full"
              >
                <div className="bg-corporate-primary-100 rounded-xl p-8 transition-all duration-300 hover:shadow-xl hover:-translate-y-2 h-full flex flex-col">
                  <div className="mb-6 flex flex-col items-center">
                    <div className="bg-corporate-secondary-100 w-16 h-16 rounded-full flex items-center justify-center mb-4 shadow-lg">
                      {step.icon}
                    </div>
                    <div className="text-corporate-secondary-100 font-bold text-3xl">
                      {index + 1}
                    </div>
                  </div>
                  <h3 className="font-bold text-xl md:text-2xl text-corporate-white mb-4 text-center">
                    {step.title}
                  </h3>
                  <p className="text-corporate-neutral-25 leading-relaxed text-center">
                    {step.description}
                  </p>
                  
                  {index < steps.length - 1 && (
                    <div className="hidden md:flex absolute top-1/3 -right-4 transform -translate-y-1/2 text-corporate-secondary-100 z-10">
                      <ArrowRight className="w-8 h-8" strokeWidth={2} />
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </motion.div>
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="flex justify-center"
          >
            <Link to="/get-started">
              <Button size="lg" className="bg-corporate-secondary-100 hover:bg-corporate-secondary-75 text-corporate-primary-100 px-8 py-6 text-lg font-semibold rounded-lg shadow-md">
                Get Started Now
                <ArrowRight className="ml-2" strokeWidth={2} />
              </Button>
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default HowItWorks;
